import numeral from "numeral"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  useMediaCategoryControllerGetMediaCategoryQuery,
  useMediaCategoryControllerPostMediaCategoriesMutation,
} from "~/ui-rtk/api/mediaCategoryApi.ts"

import useSources from "~/ui-rtk/hooks/sources"
import { calcDelta } from "~/ui-rtk/utils/chart-utils"

import { DetailsTitle } from "../components/DetailsHeader"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { H5, H6 } from "~/ui-rtk/components/ui/typography"
import { ArrowTopRightOnSquare } from "~/ui-rtk/components/ui/svg/arrow/ArrowTopRightOnSquare"
import { RocketLaunchIcon } from "@heroicons/react/24/outline"

import { usePostQubeQuery } from "./hooks/cube-query"

import {
  POST_MEDIA_CATEGORIES_OPTIONS,
  MENTIONED_MEDIA_CATEGORIES_OPTIONS,
  TCampaignAdSetAdDetailsProps,
  TPostMediaOption,
} from "../constants"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

import useDate from "~/ui-rtk/hooks/date"
import ListBox from "~/ui-rtk/components/ui/common/ListBox/ListBox"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import useAdForgeAccess from "~/ui-rtk/hooks/ad-forge-access"
import { useNavigate } from "react-router-dom"
import { MarathonImage } from "~/ui-rtk/components/atoms"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import { useAppDispatch } from "~/ui-rtk/app/hooks"
import { setIsOpen as setDrawerIsOpen } from "~/ui-rtk/app/slices/drawer.slice"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { isVideoUrl } from "~/ui-rtk/components/atoms/MarathonImage"
import { TagPanel } from "~/ui-rtk/components/ui/charts/TableVisualization/components/TagPanel"
import type { TagPill } from "~/ui-rtk/components/ui/charts/TableVisualization/components/TagPanel/TagPillGroup"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import useMediaTagAccess from "~/ui-rtk/hooks/media-tag-access"
import { useBulkGetTagAssignmentsQuery } from "~/ui-rtk/api/tagAssignmentsApi"
import {
  paidMediaApi,
  useAnalyzePostCreativeMutation,
  useGetPostCreativeAnalysisQuery,
  type AnalyzePostCreativeJobResult,
  type PostCreativeAnalysisResult,
} from "~/ui-rtk/api/paidMediaApi"
import { isAbortError, useWaitForJob } from "~/ui-rtk/hooks/useWaitForJob"
import { toast } from "react-toastify"
import CreativeAnalysisPanel from "../components/CreativeAnalysisPanel"
const dayjs = useDate()

const prefixKey = "company_media_metrics_organic_posts"

const extractYouTubeVideoId = (url: string): string | null => {
  if (!url) return null

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
    /youtube\.com\/embed\/([^&\s]+)/,
    /youtube\.com\/v\/([^&\s]+)/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

export default function PostDetails(props: TCampaignAdSetAdDetailsProps) {
  const { sources } = useSources()
  const { metric, widget, prefix } = props

  const prefixToUse = prefix ?? prefixKey
  const postCaption = metric[`${prefixToUse}.post_caption`]
  const postId = metric[`${prefixToUse}.post_id`]
  const postLink = metric[`${prefixToUse}.post_link`] as string
  const postDate = metric[`${prefixToUse}.md_date`]
  const sourceName = (
    metric[`${prefixToUse}.media_channel`] as string
  )?.toUpperCase()

  const isMentions = prefix === "company_media_metrics_mentions"

  const reach = metric[`${prefixToUse}.reach`] as number
  const follows = isMentions
    ? (metric[`${prefixToUse}.followers`] as number)
    : (metric[`${prefixToUse}.follows`] as number)
  const shares = metric[`${prefixToUse}.shares`] as number
  const saves = metric[`${prefixToUse}.saves`] as number
  const comments = metric[`${prefixToUse}.comments`] as number
  const likes = metric[`${prefixToUse}.likes`] as number
  const mediaChannel = metric[`${prefixToUse}.media_channel`] as string

  const brandValue = (metric[`${prefixToUse}.brand_value`] ??
    metric[`${prefixToUse}.brand_value_agg`]) as number
  const brandValuePerReach = reach > 0 ? brandValue / reach : 0

  let cachedImage =
    mediaChannel === "youtube"
      ? metric[`${prefixToUse}.post_preview`]
      : metric[`${prefixToUse}.md_image_url`]

  if (isMentions) {
    cachedImage = metric[`${prefix}.display_url`]
  }

  const source = sources.get(sourceName)

  const { allPostsDetails } = usePostQubeQuery(
    postId as string,
    widget,
    prefixToUse,
  )

  const postsDetails = allPostsDetails as Record<string, unknown> | undefined
  const averageBrandValue =
    ((postsDetails?.total as number) ?? 0) > 0
      ? (postsDetails?.brandValue as number) / (postsDetails?.total as number)
      : 0

  const delta = calcDelta(brandValue, averageBrandValue)

  const [captionExpanded, setExpanded] = useState(false)

  const isYouTubePost = mediaChannel === "youtube"
  const youtubeVideoId = isYouTubePost ? extractYouTubeVideoId(postLink) : null

  const [category, setCategory] = useState<string | null | undefined>(undefined)

  const { isEnabled: isDemoEnabled } = useDemoMode()
  const { hasAccess: hasBlasterAccess } = useAdForgeAccess()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Check if this is a Meta platform post (facebook or instagram) for blast functionality
  const isMetaPost = ["facebook", "instagram", "meta"].includes(
    mediaChannel?.toLowerCase() ?? "",
  )

  // Read resolved image URL from Redux cache (populated by MarathonImage component)
  const postSourceType = mediaChannel
    ? mapMediaChannelToSourceType(mediaChannel, true)
    : "FACEBOOK_AD"
  const [createMediaCategory] =
    useMediaCategoryControllerPostMediaCategoriesMutation()

  const {
    data: mediaCategory,
    isLoading: mediaCategoryLoading,
    isFetching: mediaCategoryFetching,
    refetch,
  } = useMediaCategoryControllerGetMediaCategoryQuery(
    {
      id: postId as string,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (mediaCategory?.category) {
      setCategory(mediaCategory.category)
    } else {
      setCategory(null)
    }
  }, [mediaCategory])

  const categoryUpdate = async (value: TPostMediaOption) => {
    await createMediaCategory({
      body: {
        mediaId: postId as string,
        type: isMentions ? "mention" : "post",
        channel: metric[`${prefixToUse}.media_channel`] as string,
        category: value,
      },
    })
    setCategory(value)
    await refetch()
  }

  // Organic Facebook / Instagram posts get the same AI analysis + tagging as
  // ads (mentions are other people's posts — nothing to tag or blast).
  const postIdStr = String(postId ?? "")
  const postChannel: "facebook" | "instagram" | undefined =
    mediaChannel?.toLowerCase() === "instagram"
      ? "instagram"
      : mediaChannel?.toLowerCase() === "facebook"
        ? "facebook"
        : undefined
  const canAnalyzePost = !!postChannel && !isMentions && !!postIdStr

  const isMediaTagsEnabled = useMediaTagAccess()
  const { categories: tagCategories } = useTagCategories()
  const { data: postTagAssignments, isLoading: isLoadingPostTags } =
    useBulkGetTagAssignmentsQuery(
      { itemType: "post", itemIds: [postIdStr] },
      { skip: !isMediaTagsEnabled || !canAnalyzePost },
    )
  const tagPills = useMemo<TagPill[]>(() => {
    const categoryLabelBySlug = new Map(
      tagCategories.map(c => [c.slug, c.label]),
    )
    const pills: TagPill[] = []
    for (const [slug, assignments] of Object.entries(
      postTagAssignments?.[postIdStr] ?? {},
    )) {
      for (const assignment of assignments) {
        pills.push({
          label: assignment.optionLabel,
          color: assignment.optionColor,
          categoryLabel: categoryLabelBySlug.get(slug) ?? formatTagLabel(slug),
        })
      }
    }
    return pills
  }, [postTagAssignments, postIdStr, tagCategories])

  // The analysis is an `analyze-post-creative` job (videos take ~30-90s, past
  // the 29s gateway limit): enqueue, then poll. The job saves the report on
  // the post's asset row, so it shows again the next time the drawer opens.
  const [analyzePostCreative] = useAnalyzePostCreativeMutation()
  const waitForJob = useWaitForJob()
  const { data: savedPostAnalysis } = useGetPostCreativeAnalysisQuery(
    { postId: postIdStr, channel: postChannel },
    { skip: !canAnalyzePost },
  )
  const [postAnalysis, setPostAnalysis] =
    useState<PostCreativeAnalysisResult | null>(null)
  const [isAnalyzingPost, setIsAnalyzingPost] = useState(false)
  const [isPostAnalysisSaved, setIsPostAnalysisSaved] = useState(false)
  const analysisAbortRef = useRef<AbortController | null>(null)
  useEffect(() => () => analysisAbortRef.current?.abort(), [])
  const displayedPostAnalysis =
    postAnalysis ?? savedPostAnalysis?.savedAnalysis ?? null

  // The drawer is a persistent instance: moving to another post must not
  // show (or keep waiting on) the previous post's analysis.
  const analyzedPostIdRef = useRef(postIdStr)
  useEffect(() => {
    if (analyzedPostIdRef.current === postIdStr) return
    analyzedPostIdRef.current = postIdStr
    analysisAbortRef.current?.abort()
    analysisAbortRef.current = null
    setPostAnalysis(null)
    setIsAnalyzingPost(false)
    setIsPostAnalysisSaved(false)
  }, [postIdStr])

  const handleAnalyzePost = async () => {
    if (!canAnalyzePost || isAnalyzingPost) {
      return
    }
    analysisAbortRef.current?.abort()
    const abort = new AbortController()
    analysisAbortRef.current = abort
    setIsPostAnalysisSaved(false)
    setIsAnalyzingPost(true)
    try {
      // Cube's post_link holds the video URL for video posts.
      const videoUrl =
        postLink && /^https:\/\//i.test(postLink) && isVideoUrl(postLink)
          ? postLink
          : undefined
      const imageUrl =
        typeof cachedImage === "string" &&
        /^https:\/\//i.test(cachedImage) &&
        !isVideoUrl(cachedImage)
          ? cachedImage
          : undefined
      const { jobRunId } = await analyzePostCreative({
        postId: postIdStr,
        channel: postChannel,
        ...(videoUrl ? { videoUrl } : {}),
        ...(imageUrl ? { imageUrl } : {}),
        ...(postCaption ? { caption: String(postCaption) } : {}),
      }).unwrap()
      const job = await waitForJob(jobRunId, { signal: abort.signal })
      if (job.status !== "COMPLETED") {
        throw new Error(
          job.error ??
            `Post analysis ${job.status.toLowerCase()} before it finished`,
        )
      }
      const row = (job.result as AnalyzePostCreativeJobResult | null)
        ?.results[0]
      if (!row || row.error || !row.analysis || !row.mediaType) {
        throw new Error(row?.error ?? "Failed to analyze the post")
      }
      setPostAnalysis({
        postId: row.postId,
        mediaType: row.mediaType,
        analysis: row.analysis,
        suggestedTags: row.suggestedTags ?? [],
      })
      if (row.saved) {
        setIsPostAnalysisSaved(true)
        dispatch(
          paidMediaApi.util.invalidateTags([
            { type: "AdCreativeAnalysis", id: `post:${postIdStr}` },
          ]),
        )
      } else if (row.saveError) {
        toast.error(row.saveError)
      }
    } catch (err) {
      if (isAbortError(err)) return
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : undefined)
      toast.error(message ?? "Failed to analyze the post")
    } finally {
      if (analysisAbortRef.current === abort) {
        analysisAbortRef.current = null
        setIsAnalyzingPost(false)
      }
    }
  }

  return (
    <div className="post-details w-full">
      <DetailsTitle
        id={postId as string | undefined}
        title={(postCaption as string) ?? ""}
        classes={{ container: "mb-4" }}
        demoMode={isDemoEnabled}
        hideCopyId={isMentions}
      />
      <div className="col-span-7">
        {mediaCategoryLoading || mediaCategoryFetching ? (
          <Loader />
        ) : (
          <ListBox
            options={
              isMentions
                ? MENTIONED_MEDIA_CATEGORIES_OPTIONS
                : POST_MEDIA_CATEGORIES_OPTIONS
            }
            onSelect={categoryUpdate as (value: string) => void}
            value={category ?? undefined}
            label="Category"
            placeholder="Please select a Category"
          />
        )}
      </div>
      {isMediaTagsEnabled && canAnalyzePost && (
        <Card
          rounded
          className="mt-3 bg-basic-dark-blue border border-basic-blue"
        >
          <H6 className="font-semibold mb-2">Tags</H6>
          <TagPanel
            itemId={postIdStr}
            itemType="post"
            preloadedPills={tagPills}
            categories={tagCategories}
            platform={postChannel}
            isLoadingPills={isLoadingPostTags}
          />
        </Card>
      )}
      <div className={cn("grid grid-cols-2 gap-4, mt-4")}>
        <Card
          rounded
          className="bg-background-dark border border-basic-grey-inactive p-5"
        >
          <div className="aspect-square overflow-hidden">
            {isYouTubePost && youtubeVideoId ? (
              <div className="w-full h-full">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                  title={(postCaption as string) || "YouTube video"}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="rounded"
                />
              </div>
            ) : (
              <MarathonImage
                sourceType={postSourceType}
                externalRefId={String(postId)}
                url={cachedImage as string}
                alt={postCaption as string}
                className={cn("w-full h-full", isDemoEnabled && "blur-md")}
              />
            )}
          </div>
          <div>
            <div className={cn(isDemoEnabled && "blur-sm")}>
              {!captionExpanded &&
                `${(postCaption as string)?.substring(0, 60)}...`}
              {captionExpanded && (postCaption as React.ReactNode)}
            </div>
            <div className="grid grid-cols-2 justify-between">
              <p
                className="text-white font-bold py-2 px-4 rounded cursor-pointer"
                onClick={() => setExpanded(!captionExpanded)}
              >
                {!captionExpanded && "See More"}
                {captionExpanded && "Collapse"}
              </p>
            </div>
            {}
          </div>
        </Card>
        <div>
          <H5 className="font-semibold">Brand Value</H5>
          <div className="rounded-xl border border-cyan bg-cyan/15 p-5 grid grid-cols-3 gap-2 text-sm text-cyan mt-2">
            <div>All Time:</div>
            <div className="col-span-2 grid grid-cols-2 gap-2">
              <div className="font-semibold">
                ${numeral(brandValuePerReach).format("0.00a")}
              </div>
              <div>/Reach</div>
              <div className="font-semibold">
                ${numeral(brandValue).format("0.00a")}
              </div>
              <div>Total</div>
              <div className="font-semibold">
                {delta && delta > 0 ? "+" : ""}
                {delta?.toFixed(0)}%
              </div>
              <div>Average Post</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {postLink && (
          <a
            href={postLink}
            target="_blank"
            className="flex-1 gap-2 justify-center cursor-pointer items-center p-4 blue bg-basic-dark-blue border border-basic-blue hover:bg-basic-blue flex rounded-lg text-4 line-height-4 font-semibold leading-5 transition-all active:scale-95"
          >
            <ArrowTopRightOnSquare fill="white" size={20} /> View Post
          </a>
        )}
        {hasBlasterAccess && isMetaPost && !isMentions && (
          <button
            onClick={() => {
              // Deep-link into AdBlaster's Social Posts tab with this post
              // pre-selected (consumes `?postId=`). AdBlaster owns the blast
              // flow now — the previous AdForge round-trip is gone.
              dispatch(setDrawerIsOpen(false))
              navigate(
                `${brandRoutes.AD_BLASTER.SOCIAL_POSTS}?postId=${encodeURIComponent(postId as string)}`,
              )
            }}
            className="flex-1 gap-2 justify-center cursor-pointer items-center p-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 flex rounded-lg text-4 line-height-4 font-semibold leading-5 transition-all active:scale-95 text-white"
          >
            <RocketLaunchIcon className="w-5 h-5" /> Blast Post
          </button>
        )}
      </div>
      {canAnalyzePost && (
        <CreativeAnalysisPanel
          itemType="post"
          itemId={postIdStr}
          analysis={displayedPostAnalysis}
          isAnalyzing={isAnalyzingPost}
          isSaved={isPostAnalysisSaved}
          onAnalyze={() => void handleAnalyzePost()}
          textAnalysisTitle="Caption Analysis"
        />
      )}
      <Card
        rounded
        className="bg-background-dark border border-basic-grey-inactive mt-4 p-5"
      >
        <H5 className="font-semibold">Details</H5>
        <div className="grid grid-cols-2 mt-2">
          {/* <div>
           <div className="text-sm">Content Type</div>
           <div className="font-semibold">Single Image</div>
           </div> */}
          <div>
            <div className="text-sm">Active Dates</div>
            <div className="font-semibold">
              {dayjs(postDate as string | number | Date).format("MM/DD/YY")} -
              Present
            </div>
          </div>
          <div>
            <div className="text-sm">Channel</div>
            <div className="capitalize font-semibold">{source?.name}</div>
          </div>
          {/* <div>
           <div className="text-sm">Placement</div>
           <div className="font-semibold">Feed</div>
           </div> */}
        </div>
      </Card>
      <Card
        rounded
        className="bg-background-dark border border-basic-grey-inactive mt-4 p-5"
      >
        <H5 className="font-semibold">Engagements</H5>
        <div
          className={cn(
            {
              "grid-cols-6": !isMentions,
              "grid-cols-2": isMentions,
            },
            "grid mt-2",
          )}
        >
          {isMentions && (
            <>
              <div>
                <div className="text-sm">Followers</div>
                <div className="font-semibold">
                  {numeral(follows).format("0.00a")}
                </div>
              </div>
            </>
          )}
          {!isMentions && (
            <>
              <div>
                <div className="text-sm">Reach</div>
                <div className="font-semibold">
                  {numeral(reach).format("0.00a")}
                </div>
              </div>
              <div>
                <div className="text-sm">Follows</div>
                <div className="font-semibold">
                  {numeral(follows).format("0.00a")}
                </div>
              </div>
              <div>
                <div className="text-sm">Shares</div>
                <div className="font-semibold">
                  {numeral(shares).format("0.00a")}
                </div>
              </div>
              <div>
                <div className="text-sm">Saves</div>
                <div className="font-semibold">
                  {numeral(saves).format("0.00a")}
                </div>
              </div>
            </>
          )}
          <div>
            <div className="text-sm">Comments</div>
            <div className="font-semibold">
              {numeral(comments).format("0.00a")}
            </div>
          </div>
          <div>
            <div className="text-sm">Likes</div>
            <div className="font-semibold">
              {numeral(likes).format("0.00a")}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
